/**
 * Tasks Inbox — personal action queue with thread-style detail.
 *
 * Layout:
 *   • Desktop: 2-column split — left = task list grouped by urgency
 *     (Overdue / Today / Active / Recently done), right = thread view
 *     of the selected task (comments + activity, inline reply at bottom).
 *   • Mobile: list-first, switches to thread when you tap; back button
 *     returns to list. Bottom-nav stays visible.
 *
 * Why a separate page?
 *   The Board (`/dashboard/tasks`) is the team view. The Inbox is the
 *   ME view — your personal queue. Different UI ergonomics, different
 *   default sort, same backend. Pairs perfectly with the mobile-bottom-nav.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  ChevronLeft, MessageSquare, Clock, Send, Inbox as InboxIcon, AlertCircle, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  STATUS_META, STATUS_ORDER, PRIORITY_META, PRIORITY_ORDER,
  Avatar, StatusPill, PriorityDot,
  isOverdue, formatDueDate,
} from '@/components/TaskBits';

const GROUP_ACCENT = {
  overdue: { bar: '#DC2626', label: 'text-rose-600 dark:text-rose-400' },
  today: { bar: '#D97706', label: 'text-amber-600 dark:text-amber-400' },
  active: { bar: '#5E1F8B', label: 'text-[var(--ve-violet-bright)]' },
  done: { bar: '#10B981', label: 'text-emerald-600 dark:text-emerald-400' },
};

// ─────────────────── Sidebar row ───────────────────

function InboxRow({ task, active, onClick }) {
  const overdue = isOverdue(task.due_date, task.status);
  const last = task.last_comment;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`inbox-row-${task.task_id}`}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-white/5 transition-colors ${
        active
          ? 'bg-violet-50 dark:bg-[var(--ve-violet-bright)]/10 border-l-2 border-l-[var(--ve-violet-bright)]'
          : 'hover:bg-slate-50 dark:hover:bg-white/[0.03]'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <Avatar name={task.created_by_name || task.assignee_name} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <div className={`text-sm font-bold truncate ${task.status === 'done' ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>
              {task.title}
            </div>
            {task.comment_count > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-slate-500 dark:text-white/55">
                <MessageSquare size={9} /> {task.comment_count}
              </span>
            )}
          </div>
          {last ? (
            <div className="text-[11px] text-slate-500 dark:text-white/55 truncate">
              <span className="font-semibold text-slate-700 dark:text-white/75">{last.author_name}: </span>
              {last.body}
            </div>
          ) : (
            <div className="text-[11px] text-slate-400 dark:text-white/35 italic truncate">
              No comments yet
            </div>
          )}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <StatusPill status={task.status} size="sm" />
            <PriorityDot priority={task.priority} />
            {task.due_date && (
              <span className={`text-[9px] uppercase tracking-[0.14em] font-bold inline-flex items-center gap-1 ${overdue ? 'text-rose-500 animate-pulse' : 'text-slate-500'}`}>
                <Clock size={9} />{formatDueDate(task.due_date)}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─────────────────── Thread (right pane) ───────────────────

function Thread({ task, onUpdate, onComment, onBack }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  if (!task) {
    return (
      <div className="hidden md:flex flex-1 items-center justify-center text-slate-400 dark:text-white/35 text-sm h-full">
        <div className="text-center">
          <InboxIcon size={32} className="mx-auto mb-3 opacity-50" />
          <div>Pick a task from your inbox to open the thread.</div>
        </div>
      </div>
    );
  }

  const send = async (e) => {
    e?.preventDefault?.();
    if (!comment.trim() || busy) return;
    setBusy(true);
    try {
      await onComment(task.task_id, comment.trim());
      setComment('');
    } finally {
      setBusy(false);
    }
  };

  const cycleStatus = () => {
    const idx = STATUS_ORDER.indexOf(task.status);
    onUpdate(task.task_id, { status: STATUS_ORDER[(idx + 1) % STATUS_ORDER.length] });
  };
  const cyclePriority = () => {
    const idx = PRIORITY_ORDER.indexOf(task.priority);
    onUpdate(task.task_id, { priority: PRIORITY_ORDER[(idx + 1) % PRIORITY_ORDER.length] });
  };

  // Build a merged comment+activity timeline so the thread reads like
  // a single conversation.
  const merged = [];
  for (const a of task.activity || []) {
    if (a.action === 'comment') continue; // comments are surfaced separately
    merged.push({ kind: 'event', at: a.at, payload: a });
  }
  for (const c of task.comments || []) {
    merged.push({ kind: 'comment', at: c.created_at, payload: c });
  }
  merged.sort((a, b) => (a.at > b.at ? 1 : -1));

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-[#0A0A0A]" data-testid="inbox-thread">
      {/* Header */}
      <div className="border-b border-slate-200 dark:border-white/10 px-4 sm:px-5 py-3 flex items-start gap-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="inbox-thread-back"
          className="md:hidden text-slate-500 hover:text-slate-900 dark:text-white/55 dark:hover:text-white mt-1"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">
            Thread · {task.task_id.slice(-6)}
          </div>
          <div className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mt-0.5 truncate">
            {task.title}
          </div>
        </div>
      </div>
      {/* Meta */}
      <div className="border-b border-slate-100 dark:border-white/5 px-4 sm:px-5 py-3 flex flex-wrap items-center gap-3">
        <StatusPill status={task.status} onClick={cycleStatus} testid="inbox-thread-status" />
        <PriorityDot priority={task.priority} onClick={cyclePriority} testid="inbox-thread-priority" />
        <div className="flex items-center gap-1.5">
          <Avatar name={task.assignee_name} size="sm" />
          <span className="text-[11px] text-slate-500 dark:text-white/55">
            {task.assignee_name || 'Unassigned'}
          </span>
        </div>
        {task.due_date && (
          <span className={`text-[10px] uppercase tracking-[0.14em] font-bold inline-flex items-center gap-1 ${isOverdue(task.due_date, task.status) ? 'text-rose-500' : 'text-slate-500'}`}>
            <Clock size={10} />{formatDueDate(task.due_date)}
          </span>
        )}
      </div>
      {/* Description */}
      {task.description && (
        <div className="px-4 sm:px-5 py-3 border-b border-slate-100 dark:border-white/5 text-sm text-slate-700 dark:text-white/80 whitespace-pre-wrap">
          {task.description}
        </div>
      )}
      {/* Timeline */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-3" data-testid="inbox-thread-timeline">
        {merged.length === 0 && (
          <div className="text-[11px] text-slate-400 italic">No conversation yet — start by leaving a comment below.</div>
        )}
        {merged.map((m, i) => m.kind === 'comment' ? (
          <div key={m.payload.comment_id || i} className="flex gap-2.5">
            <Avatar name={m.payload.author_name} size="md" />
            <div className="flex-1 min-w-0 bg-slate-50 dark:bg-white/[0.04] rounded-lg p-2.5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-white/55">
                  {m.payload.author_name}
                </span>
                <span className="text-[10px] text-slate-400 dark:text-white/35">
                  {new Date(m.payload.created_at).toLocaleString()}
                </span>
              </div>
              <div className="text-sm text-slate-800 dark:text-white/85 mt-1 whitespace-pre-wrap">
                {m.payload.body}
              </div>
            </div>
          </div>
        ) : (
          <div key={m.payload.event_id || i} className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-white/55 pl-1">
            <span className="h-1 w-1 rounded-full bg-slate-300 dark:bg-white/30" />
            <span className="font-bold">{m.payload.actor_name}</span>
            <span>{m.payload.action.replace(/_/g, ' ')}</span>
            {m.payload.from_value && m.payload.to_value && (
              <span><code>{m.payload.from_value}</code> → <code>{m.payload.to_value}</code></span>
            )}
            {!m.payload.from_value && m.payload.to_value && (
              <span>→ <code>{m.payload.to_value}</code></span>
            )}
            <span className="text-slate-400 dark:text-white/30 ml-auto">{new Date(m.payload.at).toLocaleString()}</span>
          </div>
        ))}
      </div>
      {/* Composer */}
      <form
        onSubmit={send}
        className="border-t border-slate-200 dark:border-white/10 px-4 sm:px-5 py-3 flex items-end gap-2 bg-white dark:bg-[#0A0A0A]"
      >
        <textarea
          rows={1}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(e); }}
          placeholder="Reply…  (⌘+Enter to send)"
          data-testid="inbox-thread-composer"
          className="flex-1 resize-none text-sm border border-slate-200 dark:border-white/10 rounded p-2 bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)] min-h-[40px] max-h-[140px]"
        />
        <button
          type="submit"
          disabled={!comment.trim() || busy}
          data-testid="inbox-thread-send"
          className="px-3 py-2 rounded bg-[var(--ve-violet-bright)] text-white hover:bg-[#7C2BAB] disabled:opacity-50"
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}

// ─────────────────── Page ───────────────────

export default function TasksInbox() {
  const [data, setData] = useState({
    groups: [],
    totals: { open_assigned_to_me: 0, overdue: 0, due_today: 0, done_recently: 0 },
  });
  const [activeId, setActiveId] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInbox = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data: payload } = await api.get('/tasks/inbox');
      setData(payload);
    } catch (err) {
      console.error('TasksInbox: fetch failed', err);
      toast.error('Could not load inbox.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Re-hydrate the active task whenever the inbox list refreshes (so the
  // thread stays in sync if a teammate adds a comment). We pull it as a
  // separate effect so opening a new task doesn't trigger a fresh /inbox call.
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effects */
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    api.get(`/tasks/${activeId}`).then(({ data: full }) => {
      if (!cancelled) setActiveTask(full);
    }).catch((err) => {
      if (!cancelled) {
        console.error('TasksInbox: thread hydrate failed', err);
      }
    });
    return () => { cancelled = true; };
  }, [activeId, data]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);
  /* eslint-enable */

  const updateTask = async (taskId, patch) => {
    try {
      const { data: updated } = await api.patch(`/tasks/${taskId}`, patch);
      if (activeId === taskId) setActiveTask(updated);
      fetchInbox();
    } catch (err) {
      console.error('TasksInbox: patch failed', err);
      toast.error(err?.response?.data?.detail || 'Update failed.');
    }
  };

  const addComment = async (taskId, body) => {
    try {
      await api.post(`/tasks/${taskId}/comments`, { body });
      const { data: updated } = await api.get(`/tasks/${taskId}`);
      if (activeId === taskId) setActiveTask(updated);
      fetchInbox();
    } catch (err) {
      console.error('TasksInbox: comment failed', err);
      toast.error('Could not post comment.');
    }
  };

  const totals = data.totals;
  const showThreadOnMobile = !!activeId;

  return (
    <div className="-m-4 sm:-m-6 md:-m-8" data-testid="tasks-inbox-page">
      <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-slate-200 dark:border-white/10">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="ve-eyebrow text-[var(--ve-violet-bright)]">My inbox</div>
            <h1 className="display text-2xl sm:text-3xl mt-1 font-bold">Your personal action queue.</h1>
          </div>
          <button
            type="button"
            onClick={fetchInbox}
            disabled={refreshing}
            className="text-[10px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/[0.04] disabled:opacity-50"
            data-testid="inbox-refresh"
          >
            <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.18em] font-bold">
          <span className="text-slate-500 dark:text-white/55" data-testid="inbox-total-open">
            <span className="text-slate-900 dark:text-white text-[13px] mr-1 normal-case tracking-normal">{totals.open_assigned_to_me}</span> open
          </span>
          {totals.overdue > 0 && (
            <span className="text-rose-500" data-testid="inbox-total-overdue">
              <span className="text-rose-600 text-[13px] mr-1 normal-case tracking-normal">{totals.overdue}</span> overdue
            </span>
          )}
          {totals.due_today > 0 && (
            <span className="text-amber-600" data-testid="inbox-total-today">
              <span className="text-[13px] mr-1 normal-case tracking-normal">{totals.due_today}</span> due today
            </span>
          )}
        </div>
      </div>

      <div className="flex h-[calc(100vh-220px)] md:h-[calc(100vh-200px)] min-h-[420px]">
        {/* LIST */}
        <aside
          className={`${showThreadOnMobile ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-[360px] md:border-r border-slate-200 dark:border-white/10 overflow-y-auto bg-white dark:bg-[#0A0A0A]`}
          data-testid="inbox-list"
        >
          {loading && <div className="text-center py-12 text-[12px] text-slate-400">Loading…</div>}
          {!loading && totals.open_assigned_to_me === 0 && totals.done_recently === 0 && (
            <div className="px-5 py-12 text-center">
              <InboxIcon size={32} className="mx-auto mb-3 opacity-40" />
              <div className="text-sm font-bold text-slate-700 dark:text-white/75">Inbox zero.</div>
              <div className="text-[11px] text-slate-500 dark:text-white/45 mt-1">
                No tasks assigned to you. New ones will land here automatically.
              </div>
            </div>
          )}
          {data.groups.map((g) => (
            g.count === 0 ? null : (
              <section key={g.key}>
                <div className="px-4 py-2 sticky top-0 bg-slate-50/95 dark:bg-white/[0.02] backdrop-blur border-b border-slate-100 dark:border-white/5 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: GROUP_ACCENT[g.key]?.bar || '#94A3B8' }} />
                  <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${GROUP_ACCENT[g.key]?.label || ''}`}>
                    {g.label}
                  </span>
                  <span className="text-[10px] text-slate-400 ml-auto">{g.count}</span>
                </div>
                {g.tasks.map((t) => (
                  <InboxRow
                    key={t.task_id}
                    task={t}
                    active={t.task_id === activeId}
                    onClick={() => setActiveId(t.task_id)}
                  />
                ))}
              </section>
            )
          ))}
        </aside>
        {/* THREAD */}
        <div className={`${showThreadOnMobile ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-w-0`}>
          <Thread
            task={activeTask}
            onUpdate={updateTask}
            onComment={addComment}
            onBack={() => { setActiveId(null); setActiveTask(null); }}
          />
        </div>
      </div>
    </div>
  );
}
